"use client";
import cn from "@/utils/cn";
import { Content } from "@prismicio/client";
import LinksNav from "./links-nav";
import MobileLinksNavContainer from "./mobile-links-nav-container";
import Link from "next/link";
import Button from "@/components/buttons";
import { useZStore } from "@/utils/z-store";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import UserProfileSidebar from "@/app/(dashboard)/dashboard/_components/UserProfileSidebar";
import { useEffect } from "react";
import axios from "axios";
import { IoSearch } from "react-icons/io5";
import trackGAEvent from "@/utils/ga";
import BrandWordmark from "../BrandWordmark";

type HeaderProps = {
  links: Content.LandingPageDocumentData["header_links"];
} & React.HTMLAttributes<HTMLDivElement>;

export default function Header({ links, className, ...props }: HeaderProps) {
  const { data, actions } = useZStore();
  const { data: session } = useSession();
  const router = useRouter();

  useEffect(() => {
    const getProfilePicture = async () => {
      try {
        const response = await axios.get("/api/profile-picture");
        actions.setProfilePicture(response.data.profilePicture);
      } catch (error) {
        console.error("Error fetching profile picture:", error);
      }
    };

    if (session && session.user.id) getProfilePicture();
  }, [actions, session]);

  const handleSignInClick = () => {
    if (session) {
      // Redirect to dashboard if user is signed in
      trackGAEvent("nav_signin_click", {});
      router.push("/dashboard/home");
    } else {
      // Open the sign-in modal if user is not signed in
      actions.openModal({ modalName: "sign-in" });
    }
  };

  return (
    <div
      className={cn(
        "flex w-full sticky top-0 bg-kd-white z-30 mx-auto shadow-lg shadow-kd-black/5 py-4 xl:pl-32  items-center justify-center h-20 md:h-[85px] xl:h-[104px]",
        "md:px-20px-6 s2xl:pr-30",
        "xl:px-20 px-6",
        className,
      )}
      {...props}
    >
      <div className="flex w-full items-center justify-between">
        <Link href={"/"}>
          <BrandWordmark className="w-[180px] md:w-[220px] xl:w-[300px]" />
        </Link>

        <div className="sxl:space-x-[70px] flex items-center justify-center space-x-3 xl:ml-20 ">
          {/* Links Nav */}
          <LinksNav
            links={links}
            className={cn(
              "hidden lg:flex md:items-center xl:space-x-[78px] md:space-x-[66px]",
            )}
          />

          <div className="hidden gap-1 md:flex md:gap-[14px] lg:flex-row ">
            {/* Search button icon */}
            <Button
              className="size-10 rounded-[12px] xl:size-12"
              variant="secondary"
              onClick={() => router.push("/resources?focus=true")}
            >
              <IoSearch
                className="size-12 rounded-[12px] text-kd-lte-blue"
                size={16}
              />
            </Button>
            {/* <Link href="?modal=true&modalName=sign-in"> */}
            <Button
              variant={session ? "primary" : "secondary"}
              className="whitespace-nowrap rounded-xl px-[13.5px] py-[7.5px] font-semibold xl:px-[16px] xl:py-[8px]"
              onClick={handleSignInClick}
              // id="nav_signin_click"
            >
              {!session ? "Sign In" : "Go to dashboard"}
            </Button>
            {/* </Link> */}
            {/* <Link href="?modal=true&modalName=start-free-trial"> */}
            {!session ? (
              <Button
                // id="header_freetrial_click"
                variant="primary"
                className="whitespace-nowrap rounded-xl px-[13.5px] py-[7.5px] font-semibold xl:px-[16px] xl:py-[8px]"
                onClick={() => {
                  trackGAEvent("header_freetrial_click", {});
                  actions.openModal({ modalName: "sign-up" });
                }}
              >
                Start for free
              </Button>
            ) : (
              <UserProfileSidebar
                userName={data?.userDetails?.userName || "User"}
                userRole={data?.userDetails?.role || "user"}
                userEmail={data?.userDetails?.email || "user"}
                profilePicture={data?.profilePicture || session?.user?.image}
                userImage={session?.user?.image}
              />
            )}
            {/* </Link> */}
          </div>

          {/* Theme toggle lives only in Settings → Appearance now, not the navbar. */}

          {/* Mobile-only: user profile (3-dots) when signed in, replaces hamburger */}
          {session && (
            <div className="md:hidden">
              <UserProfileSidebar
                userName={data?.userDetails?.userName || "User"}
                userRole={data?.userDetails?.role || "user"}
                userEmail={data?.userDetails?.email || "user"}
                profilePicture={data?.profilePicture || session?.user?.image}
                userImage={session?.user?.image}
              />
            </div>
          )}

          {/* Mobile Links Drawer — only when signed out */}
          {!session && (
            <MobileLinksNavContainer className={cn("block lg:hidden")}>
              {!data.modal.isOpen && (
                <LinksNav
                  links={links}
                  className={cn(
                    "absolute flex flex-col bg-white border  right-0 divide-y top-14 rounded-2xl w-[280px] h-[280px]a h-fit",
                  )}
                  innerClassName={cn(
                    "w-full text-left h-[60px]s pl-[26px] py-5 ",
                  )}
                  enableMobileDivider
                />
              )}
            </MobileLinksNavContainer>
          )}
        </div>
      </div>
    </div>
  );
}
